/**
 * Панель «Отправка писем» на странице «Уведомления» администратора (email-daily-limit;
 * договорённость с системным администратором 05.10.2026): счётчик писем и состояние дневного
 * лимита — страховки от неконтролируемой отправки при сбое.
 *
 * Счёт ведёт сервер по своим часам: «сегодня» — календарные сутки сервера, месяцы — календарные.
 * В «отправлено» входят только письма, принятые почтовым сервером; ошибки и письма, не
 * отправленные из-за лимита, показаны отдельно. Лимит задаётся настройкой сервера
 * (`EMAIL_DAILY_LIMIT`), из кабинета не меняется. Сверх лимита уходят только письма входа
 * (сброс пароля, приглашение в кабинет); остальные не отправляются и позже не досылаются.
 *
 * Месячный объём (`EMAIL_MONTHLY_QUOTA` — сколько писем в месяц входит в тариф почтового
 * сервиса) отправку не ограничивает: счётчик месяца показывает его («140 из 1000») и
 * предупреждает о превышении. О сработавшем лимите и о превышении объёма администраторы
 * получают запись в «колокольчик» — она ведёт сюда.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { adminApi, type EmailCounts, type EmailStats } from '@/lib/api'
import { formatIsoToRu } from '@/lib/date-input'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Hint } from '@/design-system/components/primitives/text'
import { Alert } from '@/design-system/components/feedback/alert'
import { StatCard } from '@/design-system/components/data/stat-card'
import { StatGrid } from '@/design-system/components/data/stat-grid'

/** «ошибок: 2 · не отправлено из-за лимита: 5» — только ненулевые части; пусто, если всё ушло. */
function problems(c: EmailCounts): string {
  const parts: string[] = []
  if (c.error > 0) parts.push(`ошибок: ${c.error}`)
  if (c.limited > 0) parts.push(`не отправлено из-за лимита: ${c.limited}`)
  return parts.join(' · ')
}

function ProblemLine({ label, counts }: { label: string; counts: EmailCounts }) {
  const text = problems(counts)
  if (!text) return null
  return (
    <li>
      {label} — {text}
    </li>
  )
}

export function EmailStatsPanel() {
  const [stats, setStats] = useState<EmailStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const request = useRef<AbortController | null>(null)

  const load = useCallback(() => {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setLoading(true)
    setError(null)
    adminApi.notifications
      .emailStats(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setStats(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить счётчик писем'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
    return () => request.current?.abort()
  }, [load])

  const hasProblems =
    stats !== null &&
    [stats.today, stats.month, stats.prevMonth].some((c) => c.error > 0 || c.limited > 0)

  return (
    <Card
      className="mb-4"
      header={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>Отправка писем</span>
          <Button size="sm" variant="secondary" onClick={load} disabled={loading}>
            {loading ? 'Обновление…' : 'Обновить'}
          </Button>
        </div>
      }
    >
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!stats ? (
        error ? null : (
          <p className="py-2 text-txt-secondary">Загрузка…</p>
        )
      ) : stats.delivery === 'log' ? (
        <Alert variant="info" role="status" title="Почта не подключена">
          Письма не отправляются, а только записываются в журнал. Записано сегодня:{' '}
          {stats.today.logged}, за этот месяц: {stats.month.logged}.
        </Alert>
      ) : (
        <>
          {stats.limitReached ? (
            <Alert variant="danger" className="mb-3" title="Дневной лимит писем исчерпан">
              Сегодня отправлено {stats.today.sent} при лимите {stats.dailyLimit}. До конца суток
              уходят только письма входа — сброс пароля и приглашение в кабинет. Остальные письма не
              отправляются и позже не досылаются.
            </Alert>
          ) : null}
          {stats.monthlyQuotaExceeded ? (
            <Alert
              variant="info"
              role="status"
              className="mb-3"
              title="Месячный объём писем превышен"
            >
              С начала месяца отправлено {stats.month.sent} при объёме {stats.monthlyQuota}. Письма
              продолжают уходить; сверх объёма почтовый сервис может брать плату.
            </Alert>
          ) : null}
          <StatGrid>
            <StatCard
              label="Отправлено сегодня"
              value={
                stats.dailyLimit > 0
                  ? `${stats.today.sent} из ${stats.dailyLimit}`
                  : stats.today.sent
              }
              status={stats.limitReached ? 'danger' : undefined}
            />
            <StatCard
              label="За этот месяц"
              value={
                stats.monthlyQuota > 0
                  ? `${stats.month.sent} из ${stats.monthlyQuota}`
                  : stats.month.sent
              }
              status={stats.monthlyQuotaExceeded ? 'warning' : undefined}
            />
            <StatCard label="За прошлый месяц" value={stats.prevMonth.sent} />
            <StatCard
              label="Дневной лимит"
              value={stats.dailyLimit > 0 ? stats.dailyLimit : 'нет'}
              noData={stats.dailyLimit === 0}
            />
          </StatGrid>
          {hasProblems ? (
            <ul className="mt-3 list-disc pl-5 text-13 text-txt">
              <ProblemLine label="Сегодня" counts={stats.today} />
              <ProblemLine label="За этот месяц" counts={stats.month} />
              <ProblemLine label="За прошлый месяц" counts={stats.prevMonth} />
            </ul>
          ) : null}
          <Hint className="mt-3">
            Считаются письма, принятые почтовым сервером. Сутки и месяцы — по часам сервера, сегодня{' '}
            {formatIsoToRu(stats.date)}.{' '}
            {stats.dailyLimit > 0
              ? 'Дневной лимит — страховка от неконтролируемой отправки при сбое; задаётся настройкой сервера.'
              : 'Дневной лимит отключён настройкой сервера.'}
            {stats.monthlyQuota > 0
              ? ' Месячный объём — сколько писем входит в тариф почтового сервиса; отправку он не ограничивает.'
              : ''}
          </Hint>
        </>
      )}
    </Card>
  )
}
